/**
 * `/policy/:slug` — one policy document, in full.
 *
 * NO DRAWN FRAME EXISTS FOR THIS SCREEN either; see `PolicyIndex.tsx` for what follows
 * from that and where it is reported. Nothing here is transcribed and nothing here is
 * invented: every sentence arrives on the response.
 *
 * ── THE ORDER OF THE BLOCKS IS THE POINT OF THE SCREEN ──────────────────────────
 * Title, then THE DRAFT NOTICE, then version and date, then the summary, then clauses.
 * The notice is above everything a reader would start reading, on purpose and in the
 * same order the markdown in `docs/legal/` uses. A reader who scrolls past a warning has
 * already begun reading the document as though it were final, which is the whole failure
 * this stream exists to prevent: a legal document that looks final and is not is worse
 * than an obvious placeholder, because nobody checks it again.
 *
 * `__tests__/policy.test.tsx` asserts the notice appears before the first clause in the
 * rendered markup, not merely that it is somewhere on the page.
 *
 * ── BANGLA FIRST, ENGLISH SUPPORTING, NEVER CODE-SWITCHED ───────────────────────
 * `CLAUDE.md § bilingual convention`. Each clause renders its Bangla paragraphs, then
 * its English ones underneath, in a lighter weight and a Latin face — two blocks, never
 * two halves of one sentence. Both are at 13px or above: the floor is a Bangla
 * constraint and the exemption for 11.5–12px Latin is for spec documents, not for a
 * screen a customer reads on a phone.
 */
import { useParams } from 'react-router-dom';
import { FRAME_BOX_DOCUMENT } from '../../responsive/frame.js';
import { useResource } from '../../data/resource.js';
import { SurfaceStates } from '../../states/boundary.js';
import { ScreenHeader } from '../offering/chrome.js';
import { fetchPolicyDocument, isPolicySlug, type PolicyDocumentResponse } from './api.js';

/** One Bangla paragraph. The document's own voice. */
function Bn({ children }: { children: string }): JSX.Element {
  return (
    <p className="m-0 mt-[10px] font-anek font-normal text-[14px] leading-[1.75] text-body">
      {children}
    </p>
  );
}

/** One English paragraph. Supporting, and visibly so. */
function En({ children }: { children: string }): JSX.Element {
  return (
    <p className="m-0 mt-[8px] font-manrope font-normal text-[13px] leading-[1.6] text-muted">
      {children}
    </p>
  );
}

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD, the same split
 * `routes/explore/explore.tsx` makes. The view renders what
 * `GET /v1/policy/documents/:slug` returned; the container below does the loading.
 *
 * It is what lets `__tests__/policy.test.tsx` assert the ordering guarantee this screen
 * exists for — the draft notice before the first clause — against real markup rendered
 * from the same fixture the visual gate serves.
 */
export function PolicyDocumentView({
  data,
}: {
  data: PolicyDocumentResponse | null;
}): JSX.Element {
  const doc = data?.document;
  const chrome = data?.chrome;

  return (
    <div className={`bg-sand ${FRAME_BOX_DOCUMENT} border border-border`}>
        <ScreenHeader titleBn={doc?.title_bn ?? ''} />

        <div className="px-[16px] pt-[16px] pb-[28px] flex-1">
          {/*
            ── THE DRAFT NOTICE ────────────────────────────────────────────────
            First, before the summary and before every clause. Rendered from
            `draft_notice_bn`, which the API test asserts reaches the wire non-empty on
            all four documents — a screen cannot warn with a sentence it was not sent.

            `warning-surface` behind `amber-text` on a `warning-border` frame: docs/02's
            measured amber-on-light pair, which is readable where plain amber is not.
            Flat, no radius, no gradient, like every other surface in this system.
          */}
          {doc === undefined ? null : (
            <div
              role="note"
              className="bg-warning-surface border border-warning-border px-[13px] py-[12px]"
            >
              <div className="font-anek font-semibold text-[14px] leading-[1.75] text-amber-text">
                {doc.draft_notice_bn}
              </div>
              <div className="font-manrope font-normal text-[13px] leading-[1.6] text-amber-text mt-[8px]">
                {doc.draft_notice_en}
              </div>
            </div>
          )}

          {/* Version and effective date — the two facts a consent record names. */}
          {doc === undefined ? null : (
            <div className="mt-[14px] flex gap-[16px] flex-wrap">
              <div>
                <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">
                  {chrome?.version_label_bn ?? ''}
                </div>
                <div className="font-manrope font-semibold text-[14px] leading-[1.5] text-ink [font-variant-numeric:tabular-nums]">
                  {doc.version}
                </div>
              </div>
              <div>
                <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">
                  {chrome?.effective_label_bn ?? ''}
                </div>
                <div className="font-manrope font-semibold text-[14px] leading-[1.5] text-ink [font-variant-numeric:tabular-nums]">
                  {doc.effective_from}
                </div>
              </div>
            </div>
          )}

          {doc === undefined ? null : (
            <>
              <div className="mt-[14px] font-anek font-normal text-[14px] leading-[1.75] text-body">
                {doc.summary_bn}
              </div>
              <div className="font-manrope font-normal text-[13px] leading-[1.6] text-muted mt-[6px]">
                {doc.summary_en}
              </div>
            </>
          )}

          {/* ── the clauses ───────────────────────────────────────────────── */}
          {(doc?.clauses ?? []).map((clause, i) => (
            <section
              key={clause.id}
              data-clause={clause.id}
              className="mt-[18px] pt-[16px] border-t border-border"
            >
              <h2 className="m-0 flex gap-[9px] items-baseline">
                {/*
                  The clause number, in Manrope with Latin digits. NOT in Bangla
                  numerals: H2's header records what happens when Bangla numerals are set
                  in Manrope — the face has no glyphs for them and the browser silently
                  resolves the fallback. A clause number is quoted in a dispute, so it is
                  set in a face that can actually draw it.
                */}
                <span className="font-manrope font-bold text-[14px] leading-[1.75] text-amber-text flex-none [font-variant-numeric:tabular-nums]">
                  {i + 1}
                </span>
                <span className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">
                  {clause.heading_bn}
                </span>
              </h2>
              {clause.body_bn.map((p, k) => (
                <Bn key={`bn-${String(k)}`}>{p}</Bn>
              ))}
              <div className="mt-[10px] pl-[11px] border-l border-border">
                <div className="font-manrope font-semibold text-[13px] leading-[1.6] text-muted">
                  {clause.heading_en}
                </div>
                {clause.body_en.map((p, k) => (
                  <En key={`en-${String(k)}`}>{p}</En>
                ))}
              </div>
            </section>
          ))}

          {/* ── contact ───────────────────────────────────────────────────── */}
          {doc === undefined ? null : (
            <section className="mt-[18px] pt-[16px] border-t border-border">
              <h2 className="m-0 font-anek font-semibold text-[16px] leading-[1.75] text-ink">
                {doc.contact.heading_bn}
              </h2>
              {doc.contact.body_bn.map((p, k) => (
                <Bn key={`cbn-${String(k)}`}>{p}</Bn>
              ))}
              <div className="mt-[10px] pl-[11px] border-l border-border">
                {doc.contact.body_en.map((p, k) => (
                  <En key={`cen-${String(k)}`}>{p}</En>
                ))}
              </div>
            </section>
          )}

          {/*
            ── WHAT IS NOT FILLED IN YET ───────────────────────────────────────
            The bracketed slots, listed by name where a reader will look for them. A
            reader who meets `⟦নিবন্ধিত ঠিকানা⟧` in clause 9 should be able to find out
            here that it is a known gap rather than a rendering fault.
          */}
          {doc === undefined || doc.slots.length === 0 ? null : (
            <section className="mt-[18px] pt-[16px] border-t border-border">
              <h2 className="m-0 font-anek font-semibold text-[15px] leading-[1.75] text-ink">
                {chrome?.slots_heading_bn ?? ''}
              </h2>
              <Bn>{chrome?.slots_note_bn ?? ''}</Bn>
              <ul className="mt-[8px] mb-0 pl-[18px]">
                {doc.slots.map((s) => (
                  <li
                    key={s.key}
                    className="font-anek font-normal text-[13px] leading-[1.75] text-muted"
                  >
                    {s.label_bn}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ── who still has to read this ─────────────────────────────────── */}
          {doc === undefined ? null : (
            <section className="mt-[18px] pt-[16px] border-t border-border">
              <h2 className="m-0 font-anek font-semibold text-[15px] leading-[1.75] text-ink">
                {chrome?.review_heading_bn ?? ''}
              </h2>
              <Bn>{doc.review_note_bn}</Bn>
              <div className="mt-[10px] pl-[11px] border-l border-border">
                <En>{doc.review_note_en}</En>
              </div>
            </section>
          )}

          {/*
            The content digest, in full.

            NOT decoration and not developer output. It is the value a `policy_consents`
            row stores, and printing it here is what lets a person — or a lawyer holding
            a printout — check that the text on this screen is the text somebody agreed
            to, without access to the database. `break-all` because 64 hex characters do
            not wrap at 360.
          */}
          {data === null || data === undefined ? null : (
            <div className="mt-[18px] pt-[14px] border-t border-border font-manrope font-normal text-[13px] leading-[1.6] text-muted break-all">
              sha256 · {data.content_sha256}
            </div>
          )}
      </div>
    </div>
  );
}

/** The container: resolves the slug, loads once, and hands the payload to the view. */
export function PolicyDocumentScreen(): JSX.Element {
  const { slug } = useParams();
  /**
   * An unrecognised slug is not fetched at all.
   *
   * The alternative — send it and let the server answer 404 — spends a round trip to
   * learn something `POLICY_SLUGS` already knows, and it reaches this screen as an
   * `E_PERMISSION` whose drawn state is about a founder-mode role refusal. Neither is
   * right for a typo in an address bar, so the screen renders its chrome and no
   * document, which is what it does before any response arrives anyway.
   */
  const valid = isPolicySlug(slug);
  const { data, error } = useResource<PolicyDocumentResponse | null>(
    (signal) => (valid ? fetchPolicyDocument(slug, signal) : Promise.resolve(null)),
    `policy-${slug ?? ''}`,
  );
  return (
    <SurfaceStates surface="onboarding" error={error ?? undefined}>
      <PolicyDocumentView data={data} />
    </SurfaceStates>
  );
}
